import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import { logFCI } from "@/shared/lib/fci";
import { Modal } from "@/shared/ui/ancestry";
import { createSafeContext } from "@/shared/utils/createSafeContext";

interface ModalEntry {
    render: () => ReactNode;
    title?: string;
    onOpen?: () => void;
    onClose?: () => void;
}

interface GenericModalContextValue {
    openModal: (entry: ModalEntry) => void;
    closeModal: () => void;
}

const [GenericModalContextProvider, useGenericModal] =
    createSafeContext<GenericModalContextValue>("GenericModalContext");

export { useGenericModal };

export const GenericModalProvider = ({ children }: { children: ReactNode }) => {
    const [stack, setStack] = useState<ModalEntry[]>([]);

    const openModal = useCallback((entry: ModalEntry) => {
        setStack((stack) => [...stack, entry]);
        try {
            entry.onOpen?.();
        } catch (e) {
            logFCI({ errorType: FCI_ERROR.Navigation, errorInfo: `GenericModal: onOpen callback failed: ${e}` });
        }
    }, []);

    const closeModal = useCallback(() => {
        setStack((stack) => {
            if (stack.length === 0) return stack;
            try {
                stack[stack.length - 1]?.onClose?.();
            } catch (e) {
                logFCI({ errorType: FCI_ERROR.Navigation, errorInfo: `GenericModal: onClose callback failed: ${e}` });
            }
            return stack.slice(0, -1);
        });
    }, []);

    const topModal = stack[stack.length - 1];

    return (
        <GenericModalContextProvider value={{ openModal, closeModal }}>
            {children}
            <Modal open={!!topModal} onRequestClose={() => closeModal()} title={topModal?.title}>
                {topModal?.render()}
            </Modal>
        </GenericModalContextProvider>
    );
};

interface GenericModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: () => ReactNode;
}

export const GenericModal = ({ isOpen, onClose, title, children }: GenericModalProps) => {
    const { openModal, closeModal } = useGenericModal();
    const isOpenedRef = useRef(false);

    useEffect(() => {
        if (isOpen && !isOpenedRef.current) {
            isOpenedRef.current = true;
            openModal({
                render: children,
                title,
                onClose: () => {
                    isOpenedRef.current = false;
                    onClose();
                },
            });
        } else if (!isOpen && isOpenedRef.current) {
            isOpenedRef.current = false;
            closeModal();
        }

        return () => {
            if (isOpenedRef.current) {
                isOpenedRef.current = false;
                closeModal();
            }
        };
    }, [isOpen, openModal, closeModal, onClose, title, children]);

    return null;
};
